import { useEffect, useState } from 'react';
import axios from 'axios';
import hash from 'object-hash';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { showLoader } from '../containers/globalReducer';
import { baseUrl } from '../api/env-variables';
import { useMutation, useQuery } from 'react-query';
import { chrApi, phrApi, pwsApi, chrFileUploadApi, pwsFileUploadApi } from '../api/api';

const { CancelToken } = axios;

function useAPI(url, config = {}, initialFetch = true) {
  const dispatch = useDispatch();
  const [state, setState] = useState({
    response: undefined,
    error: undefined,
    isLoading: undefined,
    success: false,
  });

  const configHash = hash(config);

  const source = CancelToken.source();

  function execute(newConfig = {}, newUrl = '') {
    setState({ ...state, isLoading: true });
    dispatch(showLoader(true));
    axios(newUrl || url, {
      ...config,
      ...newConfig,
      cancelToken: source.token,
    })
      .then((response) => {
        dispatch(showLoader(false));
        setState((a) => ({
          ...a,
          error: undefined,
          response,
          isLoading: false,
          success: true,
        }));
      })
      .catch((error) => {
        const { message = '', data: { message: dataMessage = '' } = {} } = error;
        dispatch(showLoader(false));
        if (axios.isCancel(error)) {
          console.log('Request canceled by cleanup: ', error.message);
        } else {
          toast.error(dataMessage || message || 'Something went wrong. Rest assured this error has been reported to PhyPal.');
          setState((a) => ({ ...a, error: error.response, response: undefined, isLoading: false, success: false }));
        }
      });
  }

  useEffect(() => {
    if (initialFetch) {
      execute();
    }

    return () => {
      source.cancel('useEffect cleanup.');
    };
  }, [url, configHash]);

  const { response, error, isLoading, success } = state;

  function setData(newData) {
    // Used to update state from component
    const newResponse = { ...response, data: newData };
    setState({ ...state, response: newResponse });
  }

  const data = response ? response.data : undefined;
  return { data, response, error, isLoading, setData, execute, success };
}

export default useAPI;

function userLogin(formData) {
  return axios.post(`${baseUrl}/user/login`, formData).then((res) => res.data);
}

function downloadUrl(url) {
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function fetchAccessUrl(url) {
  if (typeof url !== 'string') {
    throw new Error('url must be a string');
  }
  return phrApi('post', 's3_access_url', {
    urls: [url],
  }).then((res) => res.data);
}

function createArchive(data) {
  const { archiveName, urls = [] } = data;
  if (!archiveName || urls.length === 0) {
    throw new Error('either url or archiveName is missing');
  }
  return phrApi('post', 'archive', { archiveName, url: urls }).then((res) => res.data);
}

export function useUrlsArchive() {
  return useMutation((archiveObj) => createArchive(archiveObj), {
    mutationKey: 'urlsArchive',
    onSuccess: (data) => {
      const { url } = data;
      if (url) {
        downloadUrl(url);
      }
    },
  });
}

export function useS3AccessUrl() {
  return useMutation((url) => fetchAccessUrl(url), {
    mutationKey: 's3AccessUrl',
    onSuccess: (data = []) => {
      const [{ accessUrl }] = data;
      if (accessUrl) {
        downloadUrl(accessUrl);
      }
    },
  });
}

export function useUserLogin(onSuccess) {
  return useMutation(
    (formData) => {
      return userLogin(formData);
    },
    {
      mutationKey: 'login',
      onSuccess,
    },
  );
}

export function usePracticeList() {
  return useQuery('practicesList', () => chrApi('get', 'user/practice', null, null).then((res) => res.data));
}

function uploadFilesToS3(formData) {
  return chrFileUploadApi('upload_files', formData, null).then((res) => res.data);
}

export function useS3Upload(path, onSuccess) {
  return useMutation(
    (files) => {
      const formData = new FormData();
      formData.append('path', path);
      Array.from(files).map((file) => formData.append('file', file));
      return uploadFilesToS3(formData);
    },
    {
      mutationKey: 's3Upload',
      onSuccess,
    },
  );
}

export function usePwsApiMutation(method, url, onSuccess) {
  return useMutation((data) => pwsApi(method, url, data).then((res) => res.data), {
    mutationKey: 'pwsApiMutation',
    onSuccess,
  });
}

export function useMRRFileUpload(path, onSuccess) {
  return useMutation(
    (files) => {
      const formData = new FormData();
      formData.append('path', path);
      Array.from(files).map((file) => formData.append('file', file));
      return pwsFileUploadApi('mrr_flow_upload_files', formData).then((res) => res.data);
    },
    { mutationKey: 'MrrFileUpload', onSuccess },
  );
}

export function useProvidersList(practiceId) {
  return useQuery('providersList', () => chrApi('get', `provider/practice/${practiceId}`, null, null).then((res) => res.data), { enabled: !!practiceId });
}

export function useInsuranceDetails(onSuccess) {
  return useQuery('fetchInsuranceDetails', () => phrApi('get', 'insurance_type', null, null).then((res) => res.data), {
    onSuccess,
  });
}

export function useApptTypes(practiceId = '', providerId = '') {
  return useQuery(['fetchApptTypes', practiceId, providerId], () => phrApi('get', 'appointmenttype', null, { practiceId, providerId }).then((res) => res.data), {
    enabled: !!practiceId && !!providerId,
    notifyOnChangeProps: 'tracked',
  });
}

export function useExamLocation(practiceId, onSuccess) {
  return useQuery(['fetchExamLocations', practiceId], () => chrApi('get', `practice_location/practice/${practiceId}`, null, null).then((res) => res.data), {
    enabled: !!practiceId,
    onSuccess,
  });
}

export function useTelemedFlowUpdate(flowId, onSuccess) {
  return useMutation(
    (status) =>
      pwsApi(
        'put',
        `telemed_flow/${flowId}`,
        {
          patientStatus: status,
        },
        null,
      ),
    {
      mutationKey: 'updateTelemedFlow',
      onSuccess,
    },
  );
}

export function useTelemedLogActivity(logId, onSuccess) {
  return useMutation(
    (action) =>
      chrApi('put', `telemed_log_activity_update/${logId}`, {
        action,
      }),
    {
      mutationKey: 'updateTelemedLogActivity',
      onSuccess,
    },
  );
}

export function useExamLocationCreate(onSuccess) {
  return useMutation((formData) => chrApi('post', 'practice_location', formData).then((res) => res.data), {
    mutationKey: 'createExamLocation',
    onSuccess,
  });
}

export function useExamLocationUpdate(locationId, onSuccess) {
  return useMutation((formData) => chrApi('put', `practice_location/${locationId}`, formData).then((res) => res.data), {
    mutationKey: 'updateExamLocation',
    onSuccess,
  });
}
